import axios from 'axios';
import humps from 'humps';
import * as Sentry from '@sentry/react';
import { installAxiosInterceptor } from '@zehnmindai/embed/bff';

// docs-api is on this page's own origin (the Vite proxy in dev, nginx's
// ZEHN_BFF_UPSTREAM in the image), where docs-api's BFF session cookie goes:
// every path is on this origin (`/embed/v1/…`, `/api/v1/…`). No token is held
// or sent; the guest pages (a guest link, a guest signer's E-IMZO sign-in)
// pass their own capability in their own headers.
//
// Keys go snake_case → camelCase on the way in, and back on the way out —
// except a key that is a uuid (a map keyed by docs' uuids, such as
// `/file/index-status`'s `statuses`), which humps would mangle.
const UUID_KEY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const keepUuidKeys = (key, convert) => (UUID_KEY.test(key) ? key : convert(key));
export const camelizeKeys = (data) => humps.camelizeKeys(data, keepUuidKeys);
const decamelizeKeys = (data) => humps.decamelizeKeys(data, keepUuidKeys);

const baseAxios = axios.create({
  baseURL: '/',
  transformResponse: [...axios.defaults.transformResponse, camelizeKeys],
  transformRequest: [decamelize, ...axios.defaults.transformRequest],
});

// @zehnmindai/embed: the cookie goes along, unsafe methods carry the
// session's X-Zehn-CSRF, a refused CSRF token or a session renewed in another
// tab is read again and the request sent once more, and a 401 that stays
// tells the SessionProvider (which signs the person in again). Installed
// first, so its response half runs before the Sentry report below: a retried
// request that then succeeds is not reported.
installAxiosInterceptor(baseAxios);

// Request Interceptor
baseAxios.interceptors.request.use((config) => ({
  ...config,
  params: decamelizeKeys(config.params),
}));

// Response Interceptor for error logging
baseAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorInfo = {
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status || 'Network Error',
      data: error.response?.data || null,
    };

    Sentry.withScope((scope) => {
      scope.setTag('http.method', error.config?.method);
      scope.setTag('http.status', error.response?.status || 'Network Error');
      scope.setContext('API Error', errorInfo);
      Sentry.captureException(error);
    });

    // Reject with the original axios error so e.response is preserved
    return Promise.reject(error);
  }
);

export default baseAxios;

// For the pages nobody signs in to (a guest link, the verification page, a
// guest signer): no session, no CSRF; a guest's token goes in the call's own
// headers.
export const publicRequest = axios.create({
  baseURL: '/',
  transformResponse: [...axios.defaults.transformResponse, camelizeKeys],
  transformRequest: [decamelize, ...axios.defaults.transformRequest],
});

publicRequest.interceptors.request.use((config) => ({
  ...config,
  params: decamelizeKeys(config.params),
}));

/** A path of docs-api on this origin: `embed/v1/x` and `/embed/v1/x` are both `/embed/v1/x`. */
export const apiPath = (path) => `/${String(path || '').replace(/^\/+/, '')}`;

// Helper function for decamelizing keys in requests
function decamelize(object) {
  if (!(object && !(object instanceof File))) return object;

  if (object instanceof FormData) {
    const formData = new FormData();
    for (const [key, value] of object.entries()) {
      formData.append(humps.decamelize(key), value);
    }
    return formData;
  }

  if (typeof object === 'object') {
    return decamelizeKeys(object);
  }

  // Add a default return value to satisfy ESLint
  return object;
}
